import { useQueryClient } from '@tanstack/react-query'
import { getMutationKey } from '@trpc/react-query'
import { useState } from 'react'
import type { ReactElement } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { GoogleSignInButton } from '../auth/GoogleSignInButton'
import { useSession } from '../auth/useSession'
import { Button, InputText } from '../components'
import { HOME_PATH } from '../lib/routes'
import { googleLoginErrorMessage, loginErrorMessage } from '../lib/trpcMessages'
import { trpc } from '../trpc'
import styles from './LoginPage.module.scss'

/**
 * The sign-in screen, and where every expired session lands.
 *
 * The tokens never touch JS: `auth.login` (or `auth.loginWithGoogle`) sets the
 * httpOnly cookie pair and this page only reacts to the mutation settling.
 * Which is also why there is nothing to store on success — the next `auth.me`
 * simply succeeds. Google is offered only when `auth.config` names a client.
 */
export const LoginPage = (): ReactElement => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isAuthenticated, isLoading } = useSession()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Settles to "no Google" on failure: the password form never waits on it.
  const configQuery = trpc.auth.config.useQuery(undefined, { retry: false })
  const googleClientId = configQuery.data?.googleClientId ?? null

  /**
   * What both sign-ins do once the session cookies are set. Purge before
   * entering: a session that ended without a logout (the tablet was switched
   * off, the browser crashed) can leave the previous person's groups in memory.
   * Clearing also drops the stale `auth.me`, so the mounted observers refetch it
   * as this user.
   */
  const enter = (): void => {
    queryClient.clear()
    navigate(HOME_PATH, { replace: true })
  }

  const googleLoginMutation = trpc.auth.loginWithGoogle.useMutation({
    mutationKey: getMutationKey(trpc.auth.loginWithGoogle),
    onSuccess: enter,
    onError: (error) => setErrorMessage(googleLoginErrorMessage(error)),
  })

  const loginMutation = trpc.auth.login.useMutation({
    // tRPC keys every mutation by its own path, and `createQueryClient` skips
    // exactly this key when a mutation 401s: a wrong password is this form's
    // ANSWER, not an expired session, and invalidating `auth.me` over it would
    // tell the operator their session had expired before it ever started.
    // Written out here so the pair is greppable from the screen it protects.
    mutationKey: getMutationKey(trpc.auth.login),
    onSuccess: enter,
    onError: (error) => setErrorMessage(loginErrorMessage(error)),
  })

  // Already signed in — never show the form again.
  if (!isLoading && isAuthenticated) {
    return <Navigate to={HOME_PATH} replace />
  }

  return (
    <div className={styles['login-page']}>
      <div className={styles['login-page__card']}>
        <p className={styles['login-page__brand']}>WASP</p>
        <h1 className={styles['login-page__heading']}>Acceso a planta</h1>

        <form
          className={styles['login-page__form']}
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            setErrorMessage(null)
            loginMutation.mutate({ email, password })
          }}
        >
          <InputText
            label="Email"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />

          <InputText
            label="Contraseña"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />

          {errorMessage !== null && (
            <p role="alert" className={styles['login-page__error']}>{errorMessage}</p>
          )}

          {/* `size="lg"` — the one action of this screen, tapped with a glove. */}
          <Button
            type="submit"
            size="lg"
            fullWidth
            disabled={loginMutation.isPending}
            label={loginMutation.isPending ? 'Entrando…' : 'Entrar'}
          />
        </form>

        {googleClientId !== null
          ? (
            <div className={styles['login-page__google']}>
              <p className={styles['login-page__separator']}>o</p>
              <GoogleSignInButton
                clientId={googleClientId}
                onCredential={(idToken) => {
                  setErrorMessage(null)
                  googleLoginMutation.mutate({ idToken })
                }}
              />
            </div>
          )
          : null}
      </div>
    </div>
  )
}
